/** @jsxImportSource @emotion/react */
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Field } from 'formik'
import moment from 'moment'
import { useMemo, useState } from 'react'
import BsButton from 'react-bootstrap/Button'
import { useParams } from 'react-router-dom'
import ReactSelect from 'react-select'
import { DevicesList } from 'ts-back-end/src/business/device/dto/device-list.dto'
import { DeviceDto, DeviceInfo } from 'ts-back-end/src/business/device/dto/device.dto'
import { OrderThirdPartyDeviceOutput } from 'ts-back-end/src/business/order/dto'
import { useCreateLabDeviceMutation, useUpdateLabDeviceMutation } from 'store/services/devices'
import {
  useAssignNightOwlDeviceMutation,
  useAssignTpdDeviceAndStartShippingMutation,
  useInitiateManualShippingMutation,
  useLocalPickupMutation,
  useValidateShippingAddressMutation,
} from 'store/services/labOrder'
import CreateDeviceForm from '../../device/CreateDeviceForm'
import { checkOutHint, handleCheckOutError } from '../../device/shelfLifeNotices'
import Toast from 'components/atoms/Toast'
import { ShippingMethod } from './ShippingMethod'

interface IProps {
  thirdPartyDevice: OrderThirdPartyDeviceOutput | undefined
  currentDevice?: DeviceDto | DeviceInfo | null
  testType: string
  values: any
  setValues: (func: (p: any) => any) => void
  devices: DevicesList
  startShipping: boolean
  allowedDevices?: number[]
}

type DeviceSelectOption = {
  value: number
  label: string
}

type DeviceLabelData = {
  id?: number | string
  label?: string
  serial?: string
  mac?: string
}

const CreateDevice = ({
  id,
  orderId,
  close,
  setValues,
  allowedDevices,
  startShipping,
}: {
  id: number | undefined
  orderId: number
  close: () => void
  setValues: (func: (p: any) => any) => void
  startShipping: boolean
  allowedDevices?: number[]
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [createDevice] = useCreateLabDeviceMutation()
  const [updateDevice] = useUpdateLabDeviceMutation()
  const [assignDevice] = useAssignNightOwlDeviceMutation()
  const [assignTpdDeviceAndStartShipping] = useAssignTpdDeviceAndStartShippingMutation()
  const [validateShippingAddress] = useValidateShippingAddressMutation()

  return (
    <div className={'mt-4 col-sm-6 col-md-4'}>
      <CreateDeviceForm
        id={id ? Number(id) : undefined}
        allowedDevices={allowedDevices || [10, 13]}
        onCancel={() => close()}
        submitting={submitting}
        onSubmit={async (values) => {
          if (submitting) return

          setSubmitting(true)

          try {
            const submitValues = {
              ...values,
              ...(!values.officeId && { officeId: null }),
              statusUpdatedAt: values.statusUpdatedAt
                ? moment(values.statusUpdatedAt).format('YYYY-MM-DD')
                : undefined,
            }

            setValues((p) => ({
              ...p,
              deviceBrandId: Number(submitValues?.brandId) || -1,
            }))

            if (id) {
              await updateDevice({ id: Number(id), ...submitValues })
              close()
            } else {
              if (startShipping) {
                const validation = await validateShippingAddress({
                  orderId: Number(orderId),
                })

                if (!validation.data?.valid) {
                  Toast({
                    type: 'error',
                    label:
                      'The provided shipping address is invalid. Please verify the address and try again.',
                  })
                  return
                }

                const createdDevice = await createDevice(submitValues).unwrap()

                try {
                  await assignTpdDeviceAndStartShipping({
                    orderId: Number(orderId),
                    deviceId: createdDevice.id,
                    selectedBy: 'test',
                  }).unwrap()

                  Toast({
                    type: 'success',
                    label: 'Shipping started successfully',
                  })

                  close()
                } catch (err) {
                  setSubmitting(false)
                  handleCheckOutError(
                    err,
                    (message) => Toast({ type: 'error', label: `Error starting shipping: ${message}` }),
                    { linkToDevice: true },
                  )
                }
              } else {
                const createdDevice = await createDevice(submitValues).unwrap()
                try {
                  await assignDevice({
                    orderId: Number(orderId),
                    deviceId: createdDevice.id,
                  }).unwrap()
                  close()
                } catch (err) {
                  handleCheckOutError(
                    err,
                    (message) => Toast({ type: 'error', label: message || 'The device could not be assigned' }),
                    { linkToDevice: true },
                  )
                }
              }
            }
          } finally {
            setSubmitting(false)
          }
        }}
      />
    </div>
  )
}

const DeviceSelection = ({
  thirdPartyDevice,
  currentDevice,
  testType,
  values,
  setValues,
  allowedDevices,
  startShipping,
  devices,
}: IProps) => {
  const { orderId } = useParams()
  const [assignDevice] = useAssignNightOwlDeviceMutation()
  const [localPickup] = useLocalPickupMutation()
  const [initiateManualShipping] = useInitiateManualShippingMutation()
  const [assignTpdDeviceAndStartShipping] = useAssignTpdDeviceAndStartShippingMutation()
  const [validateShippingAddress] = useValidateShippingAddressMutation()
  const [editDevice, setEditDevice] = useState(false)
  const [resultsShown, setResultsShown] = useState(false)

  const UNAVAILABLE_DEVICE_STATES = ['RETURN_RECEIVED', 'IN_REPAIR', 'RETURN_IN_TRANSIT']

  const getLabel = (d?: DeviceLabelData | null) =>
    `Label: ${d.label?.trim()?.toUpperCase() ?? 'N/A'} / Serial #: ${
      d.serial ?? 'N/A'
    } / Mac addr: ${d.mac ?? 'N/A'}`

  const currentDeviceId = currentDevice?.id ? Number(currentDevice.id) : undefined

  const assignSelectedDevice = async (): Promise<boolean> => {
    try {
      await assignDevice({
        orderId: Number(orderId),
        deviceId: values.deviceSerial.value,
      }).unwrap()
      return true
    } catch (err) {
      handleCheckOutError(
        err,
        (message) => Toast({ type: 'error', label: message || 'The device could not be assigned' }),
        { linkToDevice: true },
      )
      return false
    }
  }

  const filteredDevices = useMemo(() => {
    return devices?.items
      ?.filter((d) => !UNAVAILABLE_DEVICE_STATES.includes(d.state) && !!d.serial)
      ?.sort((a, b) => (a.orderId && !b.orderId ? 1 : -1))
      ?.map((d) => ({
        value: d.id,
        label: `${getLabel(d)}${checkOutHint(d.shelfLife)}`,
      }))
  }, [devices])

  return (
    <div
      css={{
        width: editDevice ? '100%' : 600,
      }}>
      {!thirdPartyDevice?.serial ? (
        <>
          <div className="mt-4 d-flex">
            <ErrorOutlineIcon className={'mr-2 text-danger'} />
            <h5 className={'font-weight-600'}>Enter device information</h5>
          </div>
          <span>
            {editDevice
              ? !thirdPartyDevice?.serial
                ? 'Device not found. Please search again.'
                : ''
              : resultsShown
                ? filteredDevices?.length
                  ? 'We found devices with matching information. Select a device from the list.'
                  : 'Device not found. Please search again.'
                : 'First, enter the serial number, Mac Address or Label of the device. If a matching device is found, select it from the list.'}
          </span>
          {resultsShown ? (
            <div>
              {filteredDevices?.length ? (
                <>
                  <label
                    htmlFor="deviceSerial"
                    className={'font-weight-normal mt-2'}
                    css={{ color: '#888888' }}>
                    Select Device
                  </label>
                  <ReactSelect
                    className="basic-single w-100 mr-3 mb-3"
                    classNamePrefix="select"
                    isSearchable
                    placeholder="Select the Device"
                    name="deviceSerial"
                    onChange={(item) =>
                      setValues((p) => ({
                        ...p,
                        deviceSerial: item,
                        deviceBrandId:
                          devices?.items?.find((x) => x?.id === item?.value)?.brandId || -1,
                      }))
                    }
                    options={(filteredDevices ?? []) as DeviceSelectOption[]}
                    value={values.deviceSerial as DeviceSelectOption | null}
                  />
                  {startShipping && values.deviceSerial && (
                    <ShippingMethod
                      testType={testType}
                      values={values}
                      onShipping={async (setSubmitted) => {
                        const validation = await validateShippingAddress({
                          orderId: Number(orderId),
                        })

                        if (!validation.data?.valid) {
                          Toast({
                            type: 'error',
                            label:
                              'The provided shipping address is invalid. Please verify the address and try again.',
                          })
                          return
                        }

                        try {
                          await assignTpdDeviceAndStartShipping({
                            orderId: Number(orderId),
                            deviceId: values.deviceSerial.value,
                            selectedBy: 'test',
                          }).unwrap()
                          Toast({
                            type: 'success',
                            label: 'Shipping started successfully',
                          })
                        } catch (err) {
                          setSubmitted(false)
                          handleCheckOutError(
                            err,
                            (message) => Toast({ type: 'error', label: `Error starting shipping: ${message}` }),
                            { linkToDevice: true },
                          )
                        }
                      }}
                      onManualShipping={async () => {
                        // no shipping without the device: a refused check-out stops here
                        if (!(await assignSelectedDevice())) return
                        await initiateManualShipping({
                          orderId: Number(orderId),
                          trackingNumber: values.trackingNumber,
                          returnTrackingNumber: values.returnTrackingNumber,
                        })
                      }}
                      onLocalPickup={async () => {
                        if (!(await assignSelectedDevice())) return
                        await localPickup({
                          orderId: Number(orderId),
                        })
                      }}
                    />
                  )}
                  {!startShipping && (
                    <BsButton
                      className={'mb-2'}
                      disabled={!values.deviceSerial}
                      onClick={async () => {
                        await assignSelectedDevice()
                      }}
                      css={{
                        width: '100%',
                      }}>
                      Assign Device
                    </BsButton>
                  )}
                </>
              ) : (
                <div className="mt-3">
                  <div className="alert alert-warning mb-3" role="alert">
                    Device not found. Please search again.
                  </div>
                  <BsButton
                    variant={'outline-primary'}
                    onClick={() => {
                      setResultsShown(false)
                      setValues((p) => ({
                        ...p,
                        deviceSerial: null,
                        deviceBrandId: -1,
                      }))
                    }}
                    css={{
                      width: '100%',
                    }}>
                    Search Again
                  </BsButton>
                </div>
              )}
            </div>
          ) : (
            <>
              <div>
                <label
                  htmlFor="searchSerialNumber"
                  className={'font-weight-normal mt-2'}
                  css={{ color: '#888888' }}>
                  Serial Number
                </label>
                <Field
                  type="text"
                  name="searchSerialNumber"
                  className="form-control"
                  placeholder="Enter Serial Number"
                  value={values.searchSerialNumber}
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  htmlFor="searchMacAddress"
                  className={'font-weight-normal mt-2'}
                  css={{ color: '#888888' }}>
                  Mac Addr.
                </label>
                <Field
                  type="text"
                  name="searchMacAddress"
                  className="form-control"
                  placeholder="Enter Mac Addr."
                  value={values.searchMacAddress}
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  htmlFor="searchLabel"
                  className={'font-weight-normal mt-2'}
                  css={{ color: '#888888' }}>
                  Label
                </label>
                <Field
                  type="text"
                  name="searchLabel"
                  className="form-control"
                  placeholder="Enter Label"
                  value={values.searchLabel}
                  autoComplete="off"
                />
              </div>
              <BsButton
                className={'mt-3'}
                disabled={
                  !values.searchSerialNumber && !values.searchMacAddress && !values.searchLabel
                }
                css={{
                  width: '100%',
                }}
                onClick={() => setResultsShown(true)}>
                Check
              </BsButton>
            </>
          )}
        </>
      ) : (
        <>
          <div className="mt-4 d-flex">
            <CheckBoxOutlinedIcon className={'mr-2 text-success'} />
            <h5 className={'font-weight-600'}>Device Information</h5>
          </div>
          {editDevice ? (
            <CreateDevice
              id={currentDeviceId}
              orderId={Number(orderId)}
              setValues={setValues}
              close={() => setEditDevice(false)}
              startShipping={startShipping}
              allowedDevices={allowedDevices}
            />
          ) : (
            <span className={'d-flex align-items-center'}>
              {currentDevice
                ? getLabel(currentDevice)
                : `Serial: ${thirdPartyDevice?.serial}`}
              <BsButton
                variant={'outline-primary'}
                className={'ml-2'}
                css={{
                  height: 30,
                }}
                disabled={!!thirdPartyDevice?.transactionId}
                onClick={() => {
                  setEditDevice(true)
                }}>
                Edit
              </BsButton>
            </span>
          )}
        </>
      )}
    </div>
  )
}

export default DeviceSelection
